import { useEffect, useRef, useCallback } from "react";

import { useAtom } from "../editor-jotai";
import { t } from "../i18n";
import { recordingStateAtom } from "../recording/atoms";
import {
  createBackgroundBlurPipeline,
  isBackgroundBlurSupported,
} from "../recording/backgroundBlurPipeline";
import { getCameraStream } from "../recording/cameraStreamHelper";

import type { BackgroundBlurPipeline } from "../recording/backgroundBlurPipeline";
import {
  useDeviceEnumeration,
  requestMediaPermissions,
} from "../recording/useDeviceEnumeration";
import { useMicLevel } from "../recording/useMicLevel";
import { useRecording } from "../recording/useRecording";

import { Island } from "./Island";
import { MicLevelMeter } from "./MicLevelMeter";

import "./RecordingSettingsPanel.scss";

import type { AppClassProperties } from "../types";

type RecordingSettingsPanelProps = {
  readonly app: AppClassProperties;
};

export const RecordingSettingsPanel = ({
  app,
}: RecordingSettingsPanelProps) => {
  const [recordingState, setRecordingState] = useAtom(recordingStateAtom);
  const { cameras, microphones, refresh } = useDeviceEnumeration();
  const { startRecording, stopRecording } = useRecording();
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const blurPipelineRef = useRef<BackgroundBlurPipeline | null>(null);

  const { settings } = recordingState;

  const { level: micLevel } = useMicLevel(
    settings.micDeviceId,
    settings.micEnabled,
  );

  const updateSettings = useCallback(
    (patch: Partial<typeof settings>) => {
      setRecordingState((prev) => ({
        ...prev,
        settings: { ...prev.settings, ...patch },
      }));
    },
    [setRecordingState],
  );

  // Request permissions on mount, then refresh device list
  useEffect(() => {
    const init = async () => {
      await requestMediaPermissions();
      await refresh();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!settings.cameraEnabled) {
      blurPipelineRef.current?.destroy();
      blurPipelineRef.current = null;
      if (cameraStreamRef.current) {
        for (const track of cameraStreamRef.current.getTracks()) {
          track.stop();
        }
        cameraStreamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      return;
    }

    let cancelled = false;

    const initCamera = async () => {
      try {
        // Clean up previous pipeline and stream
        blurPipelineRef.current?.destroy();
        blurPipelineRef.current = null;
        if (cameraStreamRef.current) {
          for (const track of cameraStreamRef.current.getTracks()) {
            track.stop();
          }
        }

        const rawStream = await getCameraStream(settings.cameraDeviceId);

        if (cancelled) {
          for (const track of rawStream.getTracks()) {
            track.stop();
          }
          return;
        }

        cameraStreamRef.current = rawStream;
        let displayStream = rawStream;

        if (settings.backgroundBlur && isBackgroundBlurSupported()) {
          try {
            const pipeline = await createBackgroundBlurPipeline(rawStream);
            if (cancelled) {
              pipeline.destroy();
              return;
            }
            blurPipelineRef.current = pipeline;
            displayStream = pipeline.outputStream;
          } catch (err) {
            console.error(
              "[Recording] SettingsPanel blur init failed:",
              err,
            );
          }
        }

        if (videoRef.current) {
          videoRef.current.srcObject = displayStream;
        }

        if (!settings.cameraDeviceId && rawStream.getVideoTracks().length > 0) {
          const trackSettings = rawStream.getVideoTracks()[0].getSettings();
          if (trackSettings.deviceId) {
            updateSettings({ cameraDeviceId: trackSettings.deviceId });
          }
        }
      } catch (err) {
        console.error("[Recording] SettingsPanel camera init failed:", err);
        cameraStreamRef.current = null;
      }
    };

    initCamera();

    return () => {
      cancelled = true;
    };
  }, [settings.cameraDeviceId, settings.cameraEnabled, settings.backgroundBlur, updateSettings]);

  // Auto-select the first available microphone when mic is enabled but no device is selected
  useEffect(() => {
    if (
      settings.micEnabled &&
      !settings.micDeviceId &&
      microphones.length > 0
    ) {
      updateSettings({ micDeviceId: microphones[0].deviceId });
    }
  }, [settings.micEnabled, settings.micDeviceId, microphones, updateSettings]);

  useEffect(() => {
    return () => {
      blurPipelineRef.current?.destroy();
      blurPipelineRef.current = null;
      if (cameraStreamRef.current) {
        for (const track of cameraStreamRef.current.getTracks()) {
          track.stop();
        }
        cameraStreamRef.current = null;
      }
    };
  }, []);

  const handleStartRecording = async () => {
    const settingsSnapshot = { ...recordingState.settings };
    const positionSnapshot = { ...recordingState.cameraPosition };
    // startRecording sets status to "recording" first, which naturally
    // unmounts the settings panel. Switching tool afterwards avoids the
    // race condition where the wrapper's useEffect resets status to "idle"
    // before startRecording has a chance to run.
    try {
      await startRecording(
        app,
        settingsSnapshot,
        positionSnapshot,
        recordingState.cameraSize,
      );
      app.setActiveTool({ type: "selection" });
    } catch {
      // startRecording already resets state on failure
    }
  };

  return (
    <Island padding={3} className="recording-settings-panel">
      <h3 className="recording-settings-panel__title">
        {t("recording.settingsTitle")}
      </h3>

      <div className="recording-settings-panel__preview">
        {settings.cameraEnabled ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="recording-settings-panel__video"
            style={{
              transform: settings.cameraMirrored ? "scaleX(-1)" : "none",
            }}
          />
        ) : (
          <div className="recording-settings-panel__no-camera">
            {t("recording.cameraOff")}
          </div>
        )}
      </div>

      <div className="recording-settings-panel__field">
        <label className="recording-settings-panel__label">
          <input
            type="checkbox"
            checked={settings.cameraEnabled}
            onChange={(e) =>
              updateSettings({ cameraEnabled: e.target.checked })
            }
          />
          {t("recording.camera")}
        </label>
        {settings.cameraEnabled && cameras.length > 0 && (
          <select
            className="recording-settings-panel__select"
            value={settings.cameraDeviceId}
            onChange={(e) => updateSettings({ cameraDeviceId: e.target.value })}
          >
            {cameras.map((cam) => (
              <option key={cam.deviceId} value={cam.deviceId}>
                {cam.label}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="recording-settings-panel__field">
        <label className="recording-settings-panel__label">
          <input
            type="checkbox"
            checked={settings.micEnabled}
            onChange={(e) => updateSettings({ micEnabled: e.target.checked })}
          />
          {t("recording.recordAudio")}
        </label>
        {settings.micEnabled && microphones.length > 0 && (
          <>
            <select
              className="recording-settings-panel__select"
              value={settings.micDeviceId}
              onChange={(e) =>
                updateSettings({ micDeviceId: e.target.value })
              }
            >
              {microphones.map((mic) => (
                <option key={mic.deviceId} value={mic.deviceId}>
                  {mic.label}
                </option>
              ))}
            </select>
            <MicLevelMeter level={micLevel} />
          </>
        )}
      </div>

      <div className="recording-settings-panel__field">
        <span className="recording-settings-panel__label">
          {t("recording.cameraShape")}
        </span>
        <div className="recording-settings-panel__radio-group">
          <label>
            <input
              type="radio"
              name="cameraShape"
              value="rounded-square"
              checked={settings.cameraShape === "rounded-square"}
              onChange={() => updateSettings({ cameraShape: "rounded-square" })}
            />
            {t("recording.roundedSquare")}
          </label>
          <label>
            <input
              type="radio"
              name="cameraShape"
              value="circle"
              checked={settings.cameraShape === "circle"}
              onChange={() => updateSettings({ cameraShape: "circle" })}
            />
            {t("recording.circle")}
          </label>
        </div>
      </div>

      <div className="recording-settings-panel__field">
        <label className="recording-settings-panel__label">
          <input
            type="checkbox"
            checked={settings.cameraMirrored}
            onChange={(e) =>
              updateSettings({ cameraMirrored: e.target.checked })
            }
          />
          {t("recording.mirrorCamera")}
        </label>
      </div>

      {settings.cameraEnabled && isBackgroundBlurSupported() && (
        <div className="recording-settings-panel__field">
          <label className="recording-settings-panel__label">
            <input
              type="checkbox"
              checked={settings.backgroundBlur}
              onChange={(e) =>
                updateSettings({ backgroundBlur: e.target.checked })
              }
            />
            {t("recording.backgroundBlur")}
          </label>
        </div>
      )}

      <div className="recording-settings-panel__field">
        <label className="recording-settings-panel__label">
          <input
            type="checkbox"
            checked={settings.immersiveMode}
            onChange={(e) =>
              updateSettings({ immersiveMode: e.target.checked })
            }
          />
          {t("recording.immersiveMode")}
        </label>
      </div>

      {recordingState.status === "recording" ? (
        <button
          type="button"
          className="recording-settings-panel__start-btn"
          onClick={() => {
            stopRecording();
            app.setActiveTool({ type: "selection" });
          }}
        >
          {t("recording.stopRecording")}
        </button>
      ) : (
        <button
          type="button"
          className="recording-settings-panel__start-btn"
          onClick={handleStartRecording}
        >
          {t("recording.startRecording")}
        </button>
      )}
    </Island>
  );
};
